import { useCallback } from 'react';
import { OutlinedInput, SxProps, Theme, useTheme } from '@mui/material';
import { Field, useField } from 'formik';
import InputWrapper from '../InputWrapper';
import { important } from '../../../../utils/global/helper';
import { getInputBorderStyle } from '../../../../helpers/styles';

interface TextareaInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  tooltip?: string;
  autoHeight?: boolean;
  rows?: number;
  onlyHoverBorder?: boolean;
  noBorder?: boolean;
  sx?: SxProps<Theme>;
  controlled?: {
    value: string;
    onChange: (value: string) => void;
  };
  required?: boolean;
  noDisabledBgColor?: boolean;
  isLabelMultiline?: boolean;
}

function TextareaInput({
  name,
  label,
  placeholder,
  disabled,
  tooltip,
  autoHeight,
  rows,
  onlyHoverBorder,
  noBorder,
  sx,
  controlled,
  required,
  noDisabledBgColor,
  isLabelMultiline,
}: TextareaInputProps) {
  const [field, { error, touched }, { setTouched }] = useField(name);

  const theme = useTheme();

  const isError = !!error && !!touched;
  const hasValue = field.value && field.value.length > 0;

  const hideBorder = noBorder || (onlyHoverBorder && !!hasValue);

  const inputProps = {
    id: name,
    type: 'textarea',
    placeholder,
    disabled,
    multiline: true,
    rows: autoHeight ? undefined : rows ?? 4,
    sx: {
      height: important('auto'),
      fontSize: '0.875rem',
      '& .MuiOutlinedInput-notchedOutline': isError
        ? getInputBorderStyle({
            theme,
            hideBorder,
            isError,
          })
        : {
            borderWidth: hideBorder ? 0 : '0.5px',
            borderColor: theme.palette.lightOutlineGrey.main,
          },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: onlyHoverBorder ? '0.5px' : undefined,
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: important(noBorder ? 0 : '2px'),
        borderColor: theme.palette.primary.main,
      },
      ...(typeof sx === 'function' ? sx(theme) : sx),
    },
  };

  const onBlur = useCallback(() => setTouched(true), [setTouched]);

  return (
    <InputWrapper
      noDisabledBgColor={noDisabledBgColor}
      label={label}
      isLabelMultiline={isLabelMultiline}
      name={name}
      tooltip={tooltip}
      required={required}>
      {!controlled && (
        <Field
          name={name}
          as={OutlinedInput}
          inputProps={{
            onBlur,
          }}
          {...inputProps}
        />
      )}
      {controlled && (
        <OutlinedInput
          {...inputProps}
          value={controlled.value}
          onBlur={undefined}
          onChange={e => controlled.onChange(e.target.value)}
        />
      )}
    </InputWrapper>
  );
}

export default TextareaInput;
